/**
 * AP-28 9.3 — Artikelgruppen und Einheiten, `/articles/stammlisten`.
 *
 * Liegt **im Artikel-Ast**, nicht in den allgemeinen Einstellungen und nicht in
 * der Auswahllisten-Pflege der Zeiterfassung (Befund a): Ein Mandant ohne
 * Zeiterfassung käme sonst nicht an seine eigenen Einheiten.
 *
 * Zwei schlichte Listen — keine Listenseite nach dem Auswertungs-Standard: Es
 * sind Dutzende Einträge, keine Tausende, und niemand filtert sie.
 *
 * AP-36 Phase D: Zeilenblock. Jede Zeile ist eine Stelle (`articleGroup:<id>`,
 * `articleUnit:<id>`) und speichert beim Verlassen; die leere letzte Zeile legt
 * an. Aktivieren, Deaktivieren und Entfernen bleiben Handlungen.
 */

import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { fehlertext } from '@/lib/fehlertext'
import { api } from '@/lib/axios'
import { pageWide } from '@/lib/form-styles'
import { ZeilenBlock, ZeilenListe, Feld as Spalte, zelle, type Spalte as SpaltenDef } from '@/components/zeilenblock'
import {
  FeldStatusZeichen,
  mitStand,
  useAenderung,
  useFeld,
  useGruppe,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern'
import { articlesApi } from '../api/articles.api'
import type { ArticleGroup, ArticleUnit } from '../types/article.types'
import { bestaetige } from '@/lib/dialoge'

export default function ArticleListsPage() {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const canManage = erfuelltRecht('articles.list.manage', user?.permissions)

  const [gruppen, setGruppen] = useState<ArticleGroup[]>([])
  const [einheiten, setEinheiten] = useState<ArticleUnit[]>([])
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      const [g, u] = await Promise.all([articlesApi.groups(), articlesApi.units()])
      setGruppen(g.data.data)
      setEinheiten(u.data.data)
    } catch (err) {
      setFehler(fehlertext(err, t))
    }
  }, [t])

  useEffect(() => {
    void laden()
  }, [laden])

  /** Handlung (Anlegen, Entfernen, Samen): ausführen, neu laden, Meldung des Servers zeigen. */
  const aktion = async (schritt: () => Promise<unknown>) => {
    setFehler(null)
    try {
      await schritt()
      await laden()
      return true
    } catch (err) {
      setFehler(fehlertext(err, t))
      return false
    }
  }

  return (
    <div className={pageWide}>
      <div>
        <Link to="/articles" className="text-sm text-muted-foreground hover:text-foreground">
          ‹ {t('articles.title')}
        </Link>
        <h1 className="mt-2 text-3xl font-bold text-foreground">{t('articles.lists.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('articles.lists.subtitle')}</p>
      </div>

      {fehler && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>}

      <div className="grid gap-6 lg:grid-cols-2">
        <GruppenKarte gruppen={gruppen} canManage={canManage} aktion={aktion} />
        <EinheitenKarte einheiten={einheiten} canManage={canManage} aktion={aktion} />
      </div>
    </div>
  )
}

type Aktion = (schritt: () => Promise<unknown>) => Promise<boolean>

function Karte({ titel, hinweis, children }: { titel: string; hinweis: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="font-semibold text-foreground">{titel}</h2>
      {children}
      <p className="text-xs text-muted-foreground">{hinweis}</p>
    </section>
  )
}

/** Aktiv/inaktiv umschalten — eine Handlung auf der Stelle der Zeile. */
function StatusKnopf({ stelle, kennung, darf }: { stelle: Speicherstelle<unknown>; kennung: string; darf: boolean }) {
  const { t } = useTranslation()
  const aktiv = useStellenDaten(stelle).isActive !== false
  const status = useAenderung(stelle, `${kennung}:status`)
  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        disabled={!darf}
        onClick={() => void status.sende({ felder: ['isActive'], patch: { isActive: !aktiv }, wert: 'isActive' })}
        className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium disabled:cursor-default ${aktiv ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'}`}
        title={darf ? (aktiv ? t('articles.actions.deactivate') : t('articles.actions.activate')) : undefined}
      >
        {aktiv ? t('articles.status.active') : t('articles.status.inactive')}
      </button>
      <FeldStatusZeichen status={status.status} />
    </span>
  )
}

function Entfernen({ frage, onJa }: { frage: string; onJa: () => void }) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
      onClick={async () => {
        if (await bestaetige(frage, { ok: t('articles.actions.remove'), gefaehrlich: true })) onJa()
      }}
      title={t('articles.actions.remove')}
      aria-label={t('articles.actions.remove')}
    >
      <Trash2 size={14} />
    </button>
  )
}

// ─── Gruppen ──────────────────────────────────────────────────────────────────

function GruppenKarte({ gruppen, canManage, aktion }: { gruppen: ArticleGroup[]; canManage: boolean; aktion: Aktion }) {
  const { t } = useTranslation()
  const spalten: SpaltenDef[] = [
    { key: 'name', label: t('articles.lists.name'), spur: 'minmax(10rem,3fr)', min: 160, schmal: 'w-full' },
    { key: 'anzahl', label: t('articles.lists.articleCount'), spur: '5rem', min: 60, schmal: 'w-20', rechts: true },
    { key: 'status', label: t('articles.fields.status'), spur: '6rem', min: 80, schmal: 'w-24' },
    { key: 'aktion', label: '', spur: '2.5rem', min: 30, schmal: 'w-10' },
  ]

  return (
    <Karte titel={t('articles.lists.groups')} hinweis={t('articles.lists.groupsHint')}>
      <ZeilenListe spalten={spalten}>
        {gruppen.length === 0 && <p className="px-4 py-3 text-center text-sm text-muted-foreground">{t('articles.lists.emptyGroups')}</p>}
        {gruppen.map((g) => (
          <GruppenZeile key={g.id} gruppe={g} canManage={canManage} aktion={aktion} />
        ))}
        {canManage && <NeueGruppe anzahl={gruppen.length} aktion={aktion} />}
      </ZeilenListe>
    </Karte>
  )
}

function GruppenZeile({ gruppe: g, canManage, aktion }: { gruppe: ArticleGroup; canManage: boolean; aktion: Aktion }) {
  const { t } = useTranslation()
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `articleGroup:${g.id}`,
    bezeichnung: g.name,
    daten: g as unknown as Datensatz,
    schreibe: (patch, stand) => api.patch<Datensatz>(`/articles/groups/${g.id}`, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: `/articles/groups/${g.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const gruppe = useGruppe(stelle, { label: g.name })
  const name = useFeld(stelle, 'name', { gruppe, pflicht: true, gesperrt: !canManage, label: t('articles.lists.name') })

  return (
    <ZeilenBlock gruppe={gruppe} schluessel={g.id} gedimmt={!g.isActive}>
      <Spalte spalte="name">
        <input aria-label={t('articles.lists.name')} maxLength={100} className={zelle} data-start {...name.props} />
      </Spalte>
      <Spalte spalte="anzahl">
        <span className="tabular-nums">{g.articleCount ?? 0}</span>
      </Spalte>
      <Spalte spalte="status">
        <StatusKnopf stelle={stelle} kennung={`articleGroup:${g.id}`} darf={canManage} />
      </Spalte>
      <Spalte spalte="aktion">
        {canManage ? (
          <Entfernen frage={t('articles.lists.deleteGroupConfirm', { name: g.name })} onJa={() => void aktion(() => articlesApi.deleteGroup(g.id))} />
        ) : (
          <span />
        )}
      </Spalte>
    </ZeilenBlock>
  )
}

/** Die leere letzte Zeile: legt beim Verlassen an, sobald ein Name dasteht. */
function NeueGruppe({ anzahl, aktion }: { anzahl: number; aktion: Aktion }) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const anlegen = async () => {
    if (!name.trim()) return
    if (await aktion(() => articlesApi.createGroup({ name: name.trim(), sortOrder: (anzahl + 1) * 10 }))) setName('')
  }
  return (
    <ZeilenBlock neu onVerlassen={() => void anlegen()} onVerwerfen={() => setName('')}>
      <Spalte spalte="name">
        <input aria-label={t('articles.actions.addGroup')} maxLength={100} className={zelle} placeholder={`${t('articles.actions.addGroup')} …`} value={name} onChange={(e) => setName(e.target.value)} />
      </Spalte>
      <Spalte spalte="anzahl" leer><span /></Spalte>
      <Spalte spalte="status" leer><span /></Spalte>
      <Spalte spalte="aktion" leer><span /></Spalte>
    </ZeilenBlock>
  )
}

// ─── Einheiten ────────────────────────────────────────────────────────────────

const STELLEN = [0, 1, 2, 3]

function EinheitenKarte({ einheiten, canManage, aktion }: { einheiten: ArticleUnit[]; canManage: boolean; aktion: Aktion }) {
  const { t } = useTranslation()
  const spalten: SpaltenDef[] = [
    { key: 'code', label: t('articles.lists.code'), spur: '5.5rem', min: 70, schmal: 'w-24' },
    { key: 'name', label: t('articles.lists.name'), spur: 'minmax(8rem,3fr)', min: 130, schmal: 'w-full' },
    { key: 'stellen', label: t('articles.lists.decimals'), spur: '4.5rem', min: 60, schmal: 'w-20' },
    { key: 'anzahl', label: t('articles.lists.articleCount'), spur: '4.5rem', min: 55, schmal: 'w-20', rechts: true },
    { key: 'status', label: t('articles.fields.status'), spur: '6rem', min: 80, schmal: 'w-24' },
    { key: 'aktion', label: '', spur: '2.5rem', min: 30, schmal: 'w-10' },
  ]

  return (
    <Karte titel={t('articles.lists.units')} hinweis={t('articles.lists.unitsHint')}>
      <ZeilenListe spalten={spalten}>
        {einheiten.length === 0 && (
          <p className="px-4 py-3 text-center text-sm text-muted-foreground">
            {t('articles.lists.emptyUnits')}
            {canManage && (
              <button type="button" className="ml-2 text-primary hover:underline" onClick={() => void aktion(() => articlesApi.seedUnits())}>
                {t('articles.actions.seedUnits')}
              </button>
            )}
          </p>
        )}
        {einheiten.map((u) => (
          <EinheitZeile key={u.id} einheit={u} canManage={canManage} aktion={aktion} />
        ))}
        {canManage && <NeueEinheit anzahl={einheiten.length} aktion={aktion} />}
      </ZeilenListe>
    </Karte>
  )
}

function EinheitZeile({ einheit: u, canManage, aktion }: { einheit: ArticleUnit; canManage: boolean; aktion: Aktion }) {
  const { t } = useTranslation()
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `articleUnit:${u.id}`,
    bezeichnung: u.code,
    daten: u as unknown as Datensatz,
    schreibe: (patch, stand) => api.patch<Datensatz>(`/articles/units/${u.id}`, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: `/articles/units/${u.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const gruppe = useGruppe(stelle, { label: u.code })
  const code = useFeld(stelle, 'code', { gruppe, pflicht: true, gesperrt: !canManage, label: t('articles.lists.code') })
  const name = useFeld(stelle, 'name', { gruppe, pflicht: true, gesperrt: !canManage, label: t('articles.lists.name') })
  const stellen = useFeld<number>(stelle, 'decimals', {
    gruppe,
    art: 'auswahl',
    gesperrt: !canManage,
    label: t('articles.lists.decimals'),
    lies: (x) => Number(x),
    schreib: (w) => String(w ?? 2),
  })

  return (
    <ZeilenBlock gruppe={gruppe} schluessel={u.id} gedimmt={!u.isActive}>
      <Spalte spalte="code">
        <input aria-label={t('articles.lists.code')} maxLength={12} className={`${zelle} font-mono`} data-start {...code.props} />
      </Spalte>
      <Spalte spalte="name">
        <input aria-label={t('articles.lists.name')} maxLength={60} className={zelle} {...name.props} />
      </Spalte>
      <Spalte spalte="stellen">
        <select aria-label={t('articles.lists.decimals')} className={zelle} {...stellen.props}>
          {STELLEN.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </Spalte>
      <Spalte spalte="anzahl">
        <span className="tabular-nums">{u.articleCount ?? 0}</span>
      </Spalte>
      <Spalte spalte="status">
        <StatusKnopf stelle={stelle} kennung={`articleUnit:${u.id}`} darf={canManage} />
      </Spalte>
      <Spalte spalte="aktion">
        {canManage ? (
          <Entfernen frage={t('articles.lists.deleteUnitConfirm', { code: u.code })} onJa={() => void aktion(() => articlesApi.deleteUnit(u.id))} />
        ) : (
          <span />
        )}
      </Spalte>
    </ZeilenBlock>
  )
}

/** Die leere letzte Zeile: legt beim Verlassen an, sobald Kürzel und Bezeichnung dastehen. */
function NeueEinheit({ anzahl, aktion }: { anzahl: number; aktion: Aktion }) {
  const { t } = useTranslation()
  const leer = { code: '', name: '', decimals: 2 }
  const [neu, setNeu] = useState(leer)
  const anlegen = async () => {
    if (!neu.code.trim() || !neu.name.trim()) return
    if (await aktion(() => articlesApi.createUnit({ code: neu.code.trim(), name: neu.name.trim(), decimals: neu.decimals, sortOrder: (anzahl + 1) * 10 }))) setNeu(leer)
  }
  return (
    <ZeilenBlock neu onVerlassen={() => void anlegen()} onVerwerfen={() => setNeu(leer)}>
      <Spalte spalte="code">
        <input aria-label={t('articles.lists.code')} maxLength={12} className={`${zelle} font-mono`} placeholder={t('articles.lists.code')} value={neu.code} onChange={(e) => setNeu({ ...neu, code: e.target.value })} />
      </Spalte>
      <Spalte spalte="name" leer>
        <input aria-label={t('articles.actions.addUnit')} maxLength={60} className={zelle} placeholder={`${t('articles.actions.addUnit')} …`} value={neu.name} onChange={(e) => setNeu({ ...neu, name: e.target.value })} />
      </Spalte>
      <Spalte spalte="stellen" leer>
        <select aria-label={t('articles.lists.decimals')} className={zelle} value={neu.decimals} onChange={(e) => setNeu({ ...neu, decimals: Number(e.target.value) })}>
          {STELLEN.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </Spalte>
      <Spalte spalte="anzahl" leer><span /></Spalte>
      <Spalte spalte="status" leer><span /></Spalte>
      <Spalte spalte="aktion" leer><span /></Spalte>
    </ZeilenBlock>
  )
}
